import ButtonSimple from '@/components/HeroBlockBlog/components/ButtonSimple/ButtonSimple';
import Autocomplete from './Autocomplete/Autocomplete';
import PassengerSelect from '@/components/HeroBlockBlog/components/PassengerSelect/PassengerSelect';
import DatePicker from '@/components/HeroBlockBlog/components/DatePicker/DatePicker';
import TimePicker from '@/components/HeroBlockBlog/components/TimePicker/TimePicker';
import TypeAircraftSelect from '@/components/HeroBlockBlog/components/TypeAircraftSelect/TypeAircraftSelect';
import { Controller, Control, FieldErrors, useWatch } from 'react-hook-form';
import { BookingFormData } from '@/types/heroBlock.types';
import styles from '../booking-form.module.scss';

type TabType = 'one-way' | 'round-trip' | 'multi-city' | 'empty-leg';

interface CurrentTab1Props {
  activeTab: TabType;
  control: Control<BookingFormData>;
  multiCityInputs: string[];
  handleMultiCityInputChange: (index: number, value: string) => void;
  errors: FieldErrors<BookingFormData>;
  handleAddPoint: () => void;
  handleNext: () => Promise<void>;
  isLoading?: boolean;
}

const CurrentTab1 = ({
  activeTab,
  control,
  multiCityInputs,
  handleMultiCityInputChange,
  handleAddPoint,
  handleNext,
  isLoading = false,
}: CurrentTab1Props) => {
  // Watch departure and return dates for validation
  const departureDate = useWatch({
    control,
    name: 'departureDate',
  });

  const returnDate = useWatch({
    control,
    name: 'returnDate',
  });

  const multiCityDates = useWatch({
    control,
    name: 'multiCityDates',
  });

  const filterCyrillicFromAutocomplete = (value: string) => {
    return value.replace(/[а-яё]/gi, '');
  };

  return (
    <>
      {activeTab === 'multi-city' && (
        <div className={styles.multiCityFormContainer}>
          <div className={styles.multiCityForm}>
            <div className={styles.inputWrapper}>
              <Controller
                name={`multiCityInputs.0` as const}
                control={control}
                rules={{
                  required: 'Please enter departure city (Where from?)',
                }}
                render={({ field, fieldState }) => (
                  <div>
                    <Autocomplete
                      label="Where from?"
                      placeholder="Start typing the city"
                      value={field.value || ''}
                      onChange={(value) => {
                        const filteredValue =
                          filterCyrillicFromAutocomplete(value);
                        field.onChange(filteredValue);
                        handleMultiCityInputChange(0, filteredValue);
                      }}
                      icon="/img/heroBlock/form/trip1.svg"
                    />
                    {fieldState.error && (
                      <div className={styles.errorMessage}>
                        {fieldState.error.message}
                      </div>
                    )}
                  </div>
                )}
              />
            </div>
            <div className={styles.inputWrapper}>
              <Controller
                name={`multiCityInputs.1` as const}
                control={control}
                rules={{
                  required: 'Please enter destination city (Where to?)',
                }}
                render={({ field, fieldState }) => (
                  <div>
                    <Autocomplete
                      label="Where to?"
                      placeholder="Start typing the city"
                      value={field.value || ''}
                      onChange={(value) => {
                        const filteredValue =
                          filterCyrillicFromAutocomplete(value);
                        field.onChange(filteredValue);
                        handleMultiCityInputChange(1, filteredValue);
                      }}
                      icon="/img/heroBlock/form/trip1.svg"
                    />
                    {fieldState.error && (
                      <div className={styles.errorMessage}>
                        {fieldState.error.message}
                      </div>
                    )}
                  </div>
                )}
              />
            </div>
            <div className={styles.inputWrapper}>
              <Controller
                name={`multiCityInputs.2` as const}
                control={control}
                rules={{
                  required:
                    'Please enter at least one next destination (Where next?)',
                }}
                render={({ field, fieldState }) => (
                  <div>
                    <Autocomplete
                      label="Where next?"
                      placeholder="Start typing the city"
                      value={field.value || ''}
                      onChange={(value) => {
                        const filteredValue =
                          filterCyrillicFromAutocomplete(value);
                        field.onChange(filteredValue);
                        handleMultiCityInputChange(2, filteredValue);
                      }}
                      icon="/img/heroBlock/form/trip2.svg"
                    />
                    {fieldState.error && (
                      <div className={styles.errorMessage}>
                        {fieldState.error.message}
                      </div>
                    )}
                  </div>
                )}
              />
            </div>
          </div>
          {/* Per-leg departure date/time */}
          <div className={styles.multiCityLegsSection}>
            {Array.from({ length: multiCityInputs.length - 1 }).map((_, legIndex) => (
              <div key={legIndex} className={styles.multiCityLegRow}>
                <span className={styles.multiCityLegLabel}>
                  Leg {legIndex + 1}
                </span>
                <div className={styles.multiCityLegInputs}>
                  <div className={styles.inputWrapper}>
                    <Controller
                      name={`multiCityDates.${legIndex}` as const}
                      control={control}
                      rules={{
                        required: 'Please select departure date',
                        validate: (value) => {
                          if (legIndex === 0 || !value) return true;
                          const prevDate = multiCityDates?.[legIndex - 1];
                          if (!prevDate) return true;
                          if (new Date(value) < new Date(prevDate)) {
                            return `Must be on or after Leg ${legIndex} date`;
                          }
                          return true;
                        },
                      }}
                      render={({ field, fieldState }) => (
                        <div>
                          <DatePicker
                            label="Departure date"
                            value={field.value || ''}
                            onChange={(value) =>
                              field.onChange(filterCyrillicFromAutocomplete(value))
                            }
                          />
                          {fieldState.error && (
                            <div className={styles.errorMessage}>
                              {fieldState.error.message}
                            </div>
                          )}
                        </div>
                      )}
                    />
                  </div>
                  <div className={styles.inputWrapper}>
                    <Controller
                      name={`multiCityTimes.${legIndex}` as const}
                      control={control}
                      rules={{ required: 'Please select departure time' }}
                      render={({ field, fieldState }) => (
                        <div>
                          <TimePicker
                            label="Preferred departure time"
                            value={field.value || ''}
                            onChange={(value) =>
                              field.onChange(filterCyrillicFromAutocomplete(value))
                            }
                          />
                          {fieldState.error && (
                            <div className={styles.errorMessage}>
                              {fieldState.error.message}
                            </div>
                          )}
                        </div>
                      )}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className={styles.multiCityBottomRow}>
            <div className={styles.additionalInputs}>
              {multiCityInputs.length > 3 && (
                <>
                  {multiCityInputs
                    .slice(3)
                    .map((value: string, index: number) => (
                      <div key={index + 3} className={styles.inputWrapper}>
                        <Controller
                          name={`multiCityInputs.${index + 3}` as const}
                          control={control}
                          rules={{
                            required: 'Please fill in this destination field',
                          }}
                          render={({ field, fieldState }) => (
                            <div>
                              <Autocomplete
                                label="Where next?"
                                placeholder="Start typing the city"
                                value={field.value || ''}
                                onChange={(value) => {
                                  const filteredValue =
                                    filterCyrillicFromAutocomplete(value);
                                  field.onChange(filteredValue);
                                  handleMultiCityInputChange(
                                    index + 3,
                                    filteredValue
                                  );
                                }}
                                icon="/img/heroBlock/form/trip1.svg"
                              />
                              {fieldState.error && (
                                <div className={styles.errorMessage}>
                                  {fieldState.error.message}
                                </div>
                              )}
                            </div>
                          )}
                        />
                      </div>
                    ))}
                </>
              )}
              <div className={styles.inputWrapper}>
                <Controller
                  name="passengers"
                  control={control}
                  rules={{ required: 'Please select number of passengers' }}
                  render={({ field, fieldState }) => (
                    <div>
                      <PassengerSelect
                        label="Passenger(s)"
                        value={field.value}
                        onChange={(value) =>
                          field.onChange(filterCyrillicFromAutocomplete(value))
                        }
                        icon="/img/heroBlock/form/user.svg"
                      />
                      {fieldState.error && (
                        <div className={styles.errorMessage}>
                          {fieldState.error.message}
                        </div>
                      )}
                    </div>
                  )}
                />
              </div>
            </div>

            <div className={styles.line2}>
              <div className={styles.inputWrapper}>
                <Controller
                  name="typeAircraft"
                  control={control}
                  rules={{
                    required: 'Please select preferred type of aircraft',
                  }}
                  render={({ field, fieldState }) => (
                    <div>
                      <TypeAircraftSelect
                        label="Preferred type of aircraft"
                        value={field.value}
                        onChange={(value) =>
                          field.onChange(filterCyrillicFromAutocomplete(value))
                        }
                        icon="/img/heroBlock/form/aircraft.svg"
                      />
                      {fieldState.error && (
                        <div className={styles.errorMessage}>
                          {fieldState.error.message}
                        </div>
                      )}
                    </div>
                  )}
                />
              </div>
            </div>

            <div className={styles.buttonGroup}>
              {multiCityInputs.length < 5 && (
                <button
                  type="button"
                  className={styles.addPointButton}
                  onClick={handleAddPoint}
                >
                  Add point
                </button>
              )}
              <div onClick={!isLoading ? handleNext : undefined}>
                <ButtonSimple
                  color="accent"
                  highContrast
                  loading={isLoading}
                  size={3}
                  state="default ✦"
                  variant="solid ✦"
                  text={isLoading ? 'Sending...' : 'Next →'}
                  iconStart={false}
                  iconEnd={false}
                  focusRing={false}
                  designBlendingMode="/img/heroBlock/microphone.svg"
                  designBlendingMode1="/img/heroBlock/microphone.svg"
                />
              </div>
            </div>
          </div>
        </div>
      )}
      {activeTab === 'one-way' && (
        <>
          <div className={styles.line}>
            <div className={styles.inputWrapper}>
              <Controller
                name="fromLocation"
                control={control}
                rules={{ required: 'Please enter departure city' }}
                render={({ field, fieldState }) => (
                  <div>
                    <Autocomplete
                      label="Where from?"
                      placeholder="Start typing the city"
                      value={field.value}
                      onChange={(value) =>
                        field.onChange(filterCyrillicFromAutocomplete(value))
                      }
                      icon="/img/heroBlock/form/trip1.svg"
                    />
                    {fieldState.error && (
                      <div className={styles.errorMessage}>
                        {fieldState.error.message}
                      </div>
                    )}
                  </div>
                )}
              />
            </div>
            <div className={styles.inputWrapper}>
              <Controller
                name="toLocation"
                control={control}
                rules={{ required: 'Please enter arrival city' }}
                render={({ field, fieldState }) => (
                  <div>
                    <Autocomplete
                      label="Where to?"
                      placeholder="Start typing the city"
                      value={field.value}
                      onChange={(value) =>
                        field.onChange(filterCyrillicFromAutocomplete(value))
                      }
                      icon="/img/heroBlock/form/trip2.svg"
                    />
                    {fieldState.error && (
                      <div className={styles.errorMessage}>
                        {fieldState.error.message}
                      </div>
                    )}
                  </div>
                )}
              />
            </div>
            <div className={styles.inputWrapper}>
              <Controller
                name="passengers"
                control={control}
                rules={{ required: 'Please select number of passengers' }}
                render={({ field, fieldState }) => (
                  <div>
                    <PassengerSelect
                      label="Passenger(s)"
                      value={field.value}
                      onChange={(value) =>
                        field.onChange(filterCyrillicFromAutocomplete(value))
                      }
                      icon="/img/heroBlock/form/user.svg"
                    />
                    {fieldState.error && (
                      <div className={styles.errorMessage}>
                        {fieldState.error.message}
                      </div>
                    )}
                  </div>
                )}
              />
            </div>
          </div>

          <div className={styles.line}>
            <div className={styles.inputWrapper}>
              <Controller
                name="departureDate"
                control={control}
                rules={{ required: 'Please select departure date' }}
                render={({ field, fieldState }) => (
                  <div>
                    <DatePicker
                      label="Departure date"
                      value={field.value}
                      onChange={(value) =>
                        field.onChange(filterCyrillicFromAutocomplete(value))
                      }
                    />
                    {fieldState.error && (
                      <div className={styles.errorMessage}>
                        {fieldState.error.message}
                      </div>
                    )}
                  </div>
                )}
              />
            </div>
            <div className={styles.inputWrapper}>
              <Controller
                name="departureTime"
                control={control}
                rules={{ required: 'Please select departure time' }}
                render={({ field, fieldState }) => (
                  <div>
                    <TimePicker
                      label="Preferred departure time"
                      value={field.value}
                      onChange={(value) =>
                        field.onChange(filterCyrillicFromAutocomplete(value))
                      }
                    />
                    {fieldState.error && (
                      <div className={styles.errorMessage}>
                        {fieldState.error.message}
                      </div>
                    )}
                  </div>
                )}
              />
            </div>
            <div className={styles.inputWrapper}>
              <Controller
                name="typeAircraft"
                control={control}
                rules={{
                  required: 'Please select preferred type of aircraft',
                }}
                render={({ field, fieldState }) => (
                  <div>
                    <TypeAircraftSelect
                      label="Preferred type of aircraft"
                      value={field.value}
                      onChange={(value) =>
                        field.onChange(filterCyrillicFromAutocomplete(value))
                      }
                      icon="/img/heroBlock/form/aircraft.svg"
                    />
                    {fieldState.error && (
                      <div className={styles.errorMessage}>
                        {fieldState.error.message}
                      </div>
                    )}
                  </div>
                )}
              />
            </div>
            <div onClick={!isLoading ? handleNext : undefined}>
              <ButtonSimple
                color="accent"
                highContrast
                loading={isLoading}
                size={3}
                state="default ✦"
                variant="solid ✦"
                text={isLoading ? 'Sending...' : 'Next →'}
                iconStart={false}
                iconEnd={false}
                focusRing={false}
                designBlendingMode="/img/heroBlock/microphone.svg"
                designBlendingMode1="/img/heroBlock/microphone.svg"
                className={styles.nextButton}
              />
            </div>
          </div>
        </>
      )}
      {activeTab === 'round-trip' && (
        <>
          <div className={styles.line}>
            <div className={styles.inputWrapper}>
              <Controller
                name="fromLocation"
                control={control}
                rules={{ required: 'Please enter departure city' }}
                render={({ field, fieldState }) => (
                  <div>
                    <Autocomplete
                      label="Where from?"
                      placeholder="Start typing the city"
                      value={field.value}
                      onChange={(value) =>
                        field.onChange(filterCyrillicFromAutocomplete(value))
                      }
                      icon="/img/heroBlock/form/trip1.svg"
                    />
                    {fieldState.error && (
                      <div className={styles.errorMessage}>
                        {fieldState.error.message}
                      </div>
                    )}
                  </div>
                )}
              />
            </div>
            <div className={styles.inputWrapper}>
              <Controller
                name="toLocation"
                control={control}
                rules={{ required: 'Please enter arrival city' }}
                render={({ field, fieldState }) => (
                  <div>
                    <Autocomplete
                      label="Where to?"
                      placeholder="Start typing the city"
                      value={field.value}
                      onChange={(value) =>
                        field.onChange(filterCyrillicFromAutocomplete(value))
                      }
                      icon="/img/heroBlock/form/trip2.svg"
                    />
                    {fieldState.error && (
                      <div className={styles.errorMessage}>
                        {fieldState.error.message}
                      </div>
                    )}
                  </div>
                )}
              />
            </div>
            <div className={styles.inputWrapper}>
              <Controller
                name="passengers"
                control={control}
                rules={{ required: 'Please select number of passengers' }}
                render={({ field, fieldState }) => (
                  <div>
                    <PassengerSelect
                      label="Passenger(s)"
                      value={field.value}
                      onChange={(value) =>
                        field.onChange(filterCyrillicFromAutocomplete(value))
                      }
                      icon="/img/heroBlock/form/user.svg"
                    />
                    {fieldState.error && (
                      <div className={styles.errorMessage}>
                        {fieldState.error.message}
                      </div>
                    )}
                  </div>
                )}
              />
            </div>
          </div>

          <div className={styles.line}>
            <div className={styles.inputWrapper}>
              <Controller
                name="departureDate"
                control={control}
                rules={{
                  required: 'Please select departure date',
                  validate: (value) => {
                    if (!returnDate || !value) return true;

                    const depDate = new Date(value);
                    const retDate = new Date(returnDate);

                    if (depDate > retDate) {
                      return 'Departure date must be less than or equal to return date';
                    }
                    return true;
                  },
                }}
                render={({ field, fieldState }) => (
                  <div>
                    <DatePicker
                      label="Departure date"
                      value={field.value}
                      onChange={(value) =>
                        field.onChange(filterCyrillicFromAutocomplete(value))
                      }
                    />
                    {fieldState.error && (
                      <div className={styles.errorMessage}>
                        {fieldState.error.message}
                      </div>
                    )}
                  </div>
                )}
              />
            </div>
            <div className={styles.inputWrapper}>
              <Controller
                name="departureTime"
                control={control}
                rules={{ required: 'Please select departure time' }}
                render={({ field, fieldState }) => (
                  <div>
                    <TimePicker
                      label="Preferred departure time"
                      value={field.value}
                      onChange={(value) =>
                        field.onChange(filterCyrillicFromAutocomplete(value))
                      }
                    />
                    {fieldState.error && (
                      <div className={styles.errorMessage}>
                        {fieldState.error.message}
                      </div>
                    )}
                  </div>
                )}
              />
            </div>
            <div className={styles.inputWrapper}>
              <Controller
                name="typeAircraft"
                control={control}
                rules={{
                  required: 'Please select preferred type of aircraft',
                }}
                render={({ field, fieldState }) => (
                  <div>
                    <TypeAircraftSelect
                      label="Preferred type of aircraft"
                      value={field.value}
                      onChange={(value) =>
                        field.onChange(filterCyrillicFromAutocomplete(value))
                      }
                      icon="/img/heroBlock/form/aircraft.svg"
                    />
                    {fieldState.error && (
                      <div className={styles.errorMessage}>
                        {fieldState.error.message}
                      </div>
                    )}
                  </div>
                )}
              />
            </div>
          </div>
          <div className={styles.line}>
            <div className={styles.inputWrapper}>
              <Controller
                name="returnDate"
                control={control}
                rules={{
                  required: 'Please select return date',
                  validate: (value) => {
                    if (!departureDate || !value) return true;

                    const depDate = new Date(departureDate);
                    const retDate = new Date(value);

                    if (retDate < depDate) {
                      return 'Return date must be greater than or equal to departure date';
                    }
                    return true;
                  },
                }}
                render={({ field, fieldState }) => (
                  <div>
                    <DatePicker
                      label="Return date"
                      value={field.value}
                      onChange={(value) =>
                        field.onChange(filterCyrillicFromAutocomplete(value))
                      }
                    />
                    {fieldState.error && (
                      <div className={styles.errorMessage}>
                        {fieldState.error.message}
                      </div>
                    )}
                  </div>
                )}
              />
            </div>

            <div className={styles.inputWrapper}>
              <Controller
                name="returnTime"
                control={control}
                rules={{ required: 'Please select return time' }}
                render={({ field, fieldState }) => (
                  <div>
                    <TimePicker
                      label="Preferred return time"
                      value={field.value}
                      onChange={(value) =>
                        field.onChange(filterCyrillicFromAutocomplete(value))
                      }
                    />
                    {fieldState.error && (
                      <div className={styles.errorMessage}>
                        {fieldState.error.message}
                      </div>
                    )}
                  </div>
                )}
              />
            </div>
            <div onClick={!isLoading ? handleNext : undefined}>
              <ButtonSimple
                color="accent"
                highContrast
                loading={isLoading}
                size={3}
                state="default ✦"
                variant="solid ✦"
                text={isLoading ? 'Sending...' : 'Next →'}
                iconStart={false}
                iconEnd={false}
                focusRing={false}
                designBlendingMode="/img/heroBlock/microphone.svg"
                designBlendingMode1="/img/heroBlock/microphone.svg"
                className={styles.nextButton}
              />
            </div>
          </div>
        </>
      )}

      {activeTab === 'empty-leg' && (
        <ButtonSimple
          color="accent"
          highContrast
          loading={false}
          size={3}
          state="default ✦"
          variant="solid ✦"
          text="Search"
          iconStart={false}
          iconEnd={false}
          focusRing={false}
          designBlendingMode="/img/heroBlock/microphone.svg"
          designBlendingMode1="/img/heroBlock/microphone.svg"
        />
      )}
    </>
  );
};

export default CurrentTab1;
